import { repositories, } from 'api';

export interface StoreBasicDataApiStructure<T = any> {
    isLoading: boolean,
    isError: boolean,
    errorMessage: any,
    errorCode?: number | null,
    data: T,
}

export interface BasicApiException<T> {
    code?: number,
    message: string,
    data?: T,
}

export interface MeStateType {
    me: StoreBasicDataApiStructure<repositories.MeType>,
}

export interface ReactChildrenUtil<T = any> {
    children: T
}

export enum Breakpoints {
    XS = 'xs',
    SM = 'sm',
    MD = 'md',
    LG = 'lg',
    XL = 'xl',
}

export interface ProductsLinkType {
    self: string,
    first: string,
    last: string,
    next: string,
}

export interface ProductsItemTaxons {
    main: string,
    others: Array<string>,
}

export interface ProductsPriceType {
    current: number,
    currency: string,
}

export interface ProductsOriginalPriceType extends ProductsPriceType {}

export type ATTRIBUTES_CODES = 'school_level'
    | 'school_subjects'
    | 'book_author'
    | 'book_isbn'
    | 'book_series'
    | 'book_pages';

export interface ProductsAttributeType {
    code: ATTRIBUTES_CODES,
    name: string,
    value: string,
}

export interface ProductsDetailType {
    appUrl: string,
    flipbook: {
        image: string,
        url: string,
    },
}

export interface ProductsImageSizeType {
    size: string,
    absolutePath: string,
}

export interface ProductsImageItemType {
    path: string,
    sizes: Array<ProductsImageSizeType>,
}

export interface ProductsOriginalImageType {
    type: string,
    items: Array<ProductsImageItemType>,
}

export interface ProductsImageType {
    [key: string]: ProductsOriginalImageType,
}

export type ProductsProductItemType = 'BOOK' | 'GRANT_APP' | 'GRANT_SET' | string;

export interface ProductsLabelType {
    code: string,
    name: string,
}

export interface ProductsItemAvailabilityType {
    availableForSale: boolean,
    reason?: 'OUT_OF_STOCK',
}

export interface ProductsItemType<T = ProductsImageType> {
    code: string,
    type: ProductsProductItemType,
    name: string,
    slug: string,
    shortDescription: string,
    description: string,
    labels: Array<ProductsLabelType>,
    metaKeywords: string,
    metaDescription: string,
    taxons: ProductsItemTaxons,
    price: ProductsPriceType,
    availability: ProductsItemAvailabilityType,
    minQuantity: number,
    originalPrice?: ProductsOriginalPriceType,
    attributes: Array<ProductsAttributeType>,
    details: ProductsDetailType,
    taxAmount: number,
    images: T,
}

export interface ProductsType<T = ProductsImageType> {
    page: number,
    limit: number,
    pages: number,
    filtered?: number,
    total: number,
    _links: ProductsLinkType,
    items: Array<ProductsItemType<T>>,
}

export type BaseCurrencyUnits = {
    current: number,
    currency: 'PLN',
};

export type CartItemsTotals = {
    total : BaseCurrencyUnits,
    items: BaseCurrencyUnits,
    taxes: BaseCurrencyUnits,
    shipping: BaseCurrencyUnits,
};

export type BasicAddressDataType = {
    firstName: string,
    lastName: string,
    countryCode: string,
    street: string,
    streetNumer: string,
    city: string,
    postcode: string,
    phoneNumber: number,
};

export type ExtendedAddressDataType = {
    company: string,
    taxIdNumber: number,
};

export interface CartItemsShippingAddress {
    shippingAddress: BasicAddressDataType & ExtendedAddressDataType,
    billingAddress: BasicAddressDataType & ExtendedAddressDataType,
    payerAddress: BasicAddressDataType & ExtendedAddressDataType,
}

export type BaseCartOptionsType = {
    code: string,
    name: string,
    description: string,
};

export type CartPayments = BaseCartOptionsType & {
    instructions: string,
};

export type CartShipments = BaseCartOptionsType & {
    price: BaseCurrencyUnits,
};

export const CART_TYPE = {
  INDIVIDUAL: 'INDIVIDUAL',
  SCHOOL: 'SCHOOL',
  COUNSEL_PARENTS: 'COUNSEL_PARENTS',
} as const;

export type CartTypeEnum = 'INDIVIDUAL' | 'SCHOOL' | 'COUNSEL_PARENTS';
export type CheckoutState = 'cart' | 'addressed' | 'shipping_selected' | 'payment_selected' | 'completed';

export interface CartResponse extends CartItemsShippingAddress {
    tokenValue: string,
    type: CartTypeEnum,
    channel: string,
    currency: string,
    locale: string,
    checkoutState: CheckoutState,
    totals: CartItemsTotals,
    payments: Array<CartPayments>,
    shipments: CartShipments,
    items: Array<{
        id: number,
        product: ProductsItemType,
        quantity: number,
        total: BaseCurrencyUnits
    }>,
}

export const FILTERS_TYPE = Object.freeze({
    PRIMARY_SCHOOL: 'primary_school',
    HIGH_SCHOOL: 'high_school',
    SCHOOL_SUBJECTS: 'school_subjects',
    BOOK_TYPES: 'book_types',
    PRODUCT_TYPE: 'product_type',
    BOOK_SERIES: 'book_series'
});
